import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { fileToBase64 } from '@/modules/common/utils/file.ts';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useDeleteEntityDocumentMutation,
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { ChangeShareholderComponentProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { Dropzone } from 'ui';

interface Document {
  data: string;
  type: string;
  index?: string;
}

interface IdentificationDocumentsFormik {
  documents: Document[];
}

const validationSchema = yup.object({
  documents: yup.array().of(
    yup
      .object({
        data: yup.string().trim().required(),
        type: yup.string().trim().required(),
        index: yup.string().trim(),
      })
      .required('Please upload document'),
  ),
});
const validationSchemaIfOptional = yup.object({
  documents: yup.array().of(
    yup
      .object({
        data: yup.string().trim().required(),
        type: yup.string().trim().required(),
        index: yup.string().trim(),
      })
      .optional(),
  ),
});

export const IdentificationDocuments = ({
  moveForward,
  entity_type_prefix,
  label,
  conditionCallback,
  person_details_key,
  uuidIndex,
  item_entity_type,
  title,
  allowSkip,
  titleHint,
}: ChangeShareholderComponentProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));

  const [isFileLoading, setIsFileLoading] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');
  const docsNeeded = useMemo(() => {
    return (conditionCallback && data && conditionCallback(data)) ?? true;
  }, [data, conditionCallback]);

  const documentType = useMemo(
    () =>
      person_details_key
        ? `${item_entity_type}_entity_details_identification_document`
        : `${entity_type_prefix}_identification_document`,
    [entity_type_prefix, item_entity_type, person_details_key],
  );

  const getInitialValues = (): IdentificationDocumentsFormik => {
    return {
      documents: (data?.entity?.document_files || [])
        ?.filter(
          item =>
            (item.type === documentType && !person_details_key) || //it's correct for multi company
            (item.index === `${person_details_key}_${uuidIndex}` &&
              !!person_details_key &&
              !!uuidIndex),
        )
        ?.map(item => ({
          data: item.url ?? '',
          type: item.type ?? '',
          index: item.index ?? '',
        })),
    };
  };

  const formik = useFormik<IdentificationDocumentsFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: docsNeeded ? validationSchema : validationSchemaIfOptional,
    initialValues: getInitialValues(),
    onSubmit: async values => {
      if (docsNeeded) {
        if (values.documents.length < 1) {
          formik.setFieldError('documents', 'Please upload document');
          return;
        }
      }

      const documents =
        values?.documents?.filter(item => !item.data?.toLowerCase()?.startsWith('http')) ?? [];
      if (documents?.length > 0) {
        await updateEntity({
          ...data?.entity,
          documents,
        });
      }
      moveForward && moveForward();
    },
  });

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const { mutateAsync: deleteDocument, isLoading: isDocumentDeletion } =
    useDeleteEntityDocumentMutation();

  const onFileAccepted = useCallback(
    async (file: File) => {
      setIsFileLoading(true);

      const base64 = await fileToBase64(file, true);

      await formik.setFieldTouched('documents', true);
      await formik.setFieldValue('documents', [
        {
          type: documentType,
          data: base64,
        },
      ]);
      formik.setFieldError('documents', '');

      setIsFileLoading(false);
    },
    [documentType, formik],
  );

  const onFileAcceptedForSubRoads = useCallback(
    async (file: File) => {
      setIsFileLoading(true);

      const base64 = await fileToBase64(file, true);

      await formik.setFieldValue('documents', [
        {
          type: documentType,
          index: `${person_details_key}_${uuidIndex}`,
          data: base64,
        },
      ]);
      await formik.setFieldTouched('documents', true);
      formik.setFieldError('documents', '');

      setIsFileLoading(false);
    },
    [documentType, formik, person_details_key, uuidIndex],
  );

  const onFileClear = useCallback(async () => {
    const current = formik.values.documents.find(doc => doc.type === documentType);
    if (current?.data?.toLowerCase()?.startsWith('http')) {
      const index =
        person_details_key && uuidIndex ? `${person_details_key}_${uuidIndex}` : undefined;
      await deleteDocument({ entity_id: data?.entity.id || 0, type: documentType, index });
    }

    await formik.setFieldTouched('documents', true);
    await formik.setFieldValue('documents', []);
    formik.setFieldError('documents', '');
    setDocumentUrl('');
  }, [data?.entity.id, deleteDocument, documentType, formik, person_details_key, uuidIndex]);

  useEffect(() => {
    if (data && data.entity.document_files && !person_details_key) {
      setDocumentUrl(
        data.entity.document_files.find(({ type }) => type === documentType)?.url || '',
      );
    } else if (data && data.entity.document_files && person_details_key && uuidIndex) {
      setDocumentUrl(
        data.entity.document_files.find(
          ({ index }) => index === `${person_details_key}_${uuidIndex}`,
        )?.url || '',
      );
    }
  }, [data, uuidIndex, person_details_key, entity_type_prefix, documentType]);

  return (
    <OnboardingWrapper
      title={title || 'Identification documents'}
      titleHint={titleHint}
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? moveForward : undefined}
      isLoading={isLoading || isFileLoading}
    >
      <div className="w-full flex flex-col space-y-4">
        <div className="w-full flex flex-col space-y-1">
          <p className="text-xl">
            {label} {!docsNeeded && '(Optional)'}
          </p>
          <p className="text-sm text-gray-600">JPG, PNG or PDF. Up to 10 mb</p>
        </div>
        <Dropzone
          onFileAccepted={person_details_key ? onFileAcceptedForSubRoads : onFileAccepted}
          onFileClear={onFileClear}
          value={documentUrl || formik.values.documents[0]?.data}
          error={formik.errors.documents as string}
          loading={isFileLoading || isDocumentDeletion}
        />
      </div>
    </OnboardingWrapper>
  );
};
